import React, { useEffect, useState, type PropsWithChildren } from 'react';
import { FormattedMessage } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { Text } from '@atlaskit/primitives/compiled';
import {
	PopoverProvider,
	PopoverTarget,
	SpotlightActions,
	SpotlightBody,
	SpotlightCard,
	SpotlightControls,
	SpotlightDismissControl,
	SpotlightFooter,
	SpotlightHeader,
	SpotlightHeadline,
	SpotlightPrimaryAction,
	UNSAFE_UpdateOnChange as SpotlightUpdateOnChange,
} from '@atlaskit/spotlight';

import { useChoreographerAssets } from '@confluence/choreographer-services/entry-points/useChoreographerAssets.ts';

import { i18n } from './creation-focused-nav-i18n.ts';
import { useCfind4473CreationFocusedNav } from './useCfind4473CreationFocusedNav.ts';

export const AddFeaturesSidebarSpotlight = ({
	isCollapsed,
	children,
}: PropsWithChildren<{ isCollapsed?: boolean }>) => {
	const { isSpotlightVisible, onSpotlightShown } = useCfind4473CreationFocusedNav();
	const { PopoverContent } = useChoreographerAssets();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const [hasDismissed, setHasDismissed] = useState(false);
	const [shouldShowSpotlight, setShouldShowSpotlight] = useState(false);

	useEffect(() => {
		if (isSpotlightVisible) {
			setShouldShowSpotlight(true);
		}
	}, [isSpotlightVisible]);

	const onDismiss = (reason: string) => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'dismissedCreationFocusedNavSpotlight',
				source: 'creation-focused-nav-button-spotlight',
				attributes: { reason },
			},
		}).fire();
		setHasDismissed(true);
	};

	const onShown = () => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'shown',
				actionSubject: 'spotlight',
				actionSubjectId: 'creationFocusedNavSpotlight',
				source: 'creation-focused-nav-button-spotlight',
			},
		}).fire();
		onSpotlightShown?.();
	};

	const isVisible = !isCollapsed && !hasDismissed && shouldShowSpotlight;

	if (!isVisible) {
		return <>{children}</>;
	}

	return (
		<PopoverProvider>
			<PopoverTarget>{children}</PopoverTarget>
			<SpotlightUpdateOnChange selectors={['nav#side-navigation']} />
			<PopoverContent
				messageId="creation-focused-nav-more-features-spotlight"
				messageType="engagement"
				placement="right-end"
				dismiss={() => onDismiss('auto')}
				isVisible={isVisible}
				onMessageDisposition={(status) => {
					if (status === 'started') {
						onShown();
					}
				}}
			>
				<SpotlightCard testId="spotlight">
					<SpotlightHeader>
						<SpotlightHeadline>
							<FormattedMessage {...i18n.weAddedFeatures} />
						</SpotlightHeadline>
						<SpotlightControls>
							<SpotlightDismissControl onClick={() => onDismiss('dismiss-control')} />
						</SpotlightControls>
					</SpotlightHeader>
					<SpotlightBody>
						<Text>
							<FormattedMessage
								{...i18n.toChangeYourView}
								values={{
									menuName: (
										<Text as="strong">
											<FormattedMessage {...i18n.more} />
										</Text>
									),
								}}
							/>
						</Text>
					</SpotlightBody>
					<SpotlightFooter>
						<SpotlightActions>
							<SpotlightPrimaryAction onClick={() => onDismiss('primary-action')}>
								<FormattedMessage {...i18n.gotIt} />
							</SpotlightPrimaryAction>
						</SpotlightActions>
					</SpotlightFooter>
				</SpotlightCard>
			</PopoverContent>
		</PopoverProvider>
	);
};
